import { useEffect, useState } from 'react';
import {
  clearStoredConsent,
  getStoredConsent,
  recordConsentDecision,
} from '../analytics.ts';

/**
 * GDPR / ePrivacy consent banner with granular per-purpose toggles.
 *
 * Three top-level actions (all equally weighted — no dark pattern):
 *   - Reject non-essential → analytics: false, ads: false
 *   - Customize          → opens the granular panel with separate Analytics + Ads switches
 *   - Accept all         → analytics: true, ads: true
 *
 * The granular path matters because EDPB and CNIL increasingly expect users to consent to
 * categories independently. The data layer (`recordConsentDecision`) already supports it —
 * the cookie stores `{ a, m, t, v }` so a customized decision survives reload.
 *
 * Re-open path: any element can dispatch `new Event('eaa-open-consent')` to clear the
 * stored decision and force the banner to reappear — used by the footer "Cookie settings"
 * link.
 */
export default function ConsentBanner() {
  // `mounted` defers any render until after the first client-side useEffect runs. Combined
  // with an explicit puppeteer-prerender bail-out below, this guarantees the banner DOM is
  // NEVER present in the prerendered HTML — so a returning visitor (who already consented)
  // doesn't see a brief flash of "banner visible" between document paint and React's cookie
  // check on every page load.
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState<boolean>(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    // Skip during prerender pass. scripts/prerender.mjs drives Chromium via puppeteer, whose
    // default UA contains "HeadlessChrome". Without this guard the banner ends up baked into
    // every prerendered HTML file and creates a paint-then-hide flash on every direct URL hit.
    if (typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent)) {
      return;
    }
    setMounted(true);
    setShow(getStoredConsent() === null);
  }, []);

  useEffect(() => {
    const onOpen = () => {
      clearStoredConsent();
      const prior = getStoredConsent();
      setAnalytics(prior?.analytics ?? false);
      setAds(prior?.ads ?? false);
      setCustomizing(false);
      setShow(true);
    };
    window.addEventListener('eaa-open-consent', onOpen);
    return () => window.removeEventListener('eaa-open-consent', onOpen);
  }, []);

  function decide(decisionAnalytics: boolean, decisionAds: boolean) {
    recordConsentDecision({ analytics: decisionAnalytics, ads: decisionAds });
    setShow(false);
    setCustomizing(false);
  }

  if (!mounted || !show) return null;

  return (
    <div className="consent-banner" role="region" aria-label="Cookie consent">
      <div className="consent-banner-text">
        <p>
          We use cookies for site analytics and to measure ad performance. Choose what you're
          comfortable with — only strictly-necessary cookies are set without your consent.{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy policy
          </a>
          .
        </p>
      </div>

      {customizing ? (
        <div className="consent-customize">
          <fieldset className="consent-option">
            <label>
              <input
                type="checkbox"
                checked={true}
                disabled
                aria-describedby="consent-essential-desc"
              />
              <span className="consent-option-title">Strictly necessary</span>
            </label>
            <p id="consent-essential-desc" className="consent-option-desc">
              Required for sign-in and CSRF protection. Always on; cannot be disabled.
            </p>
          </fieldset>

          <fieldset className="consent-option">
            <label>
              <input
                type="checkbox"
                checked={analytics}
                onChange={e => setAnalytics(e.target.checked)}
                aria-describedby="consent-analytics-desc"
              />
              <span className="consent-option-title">Analytics</span>
            </label>
            <p id="consent-analytics-desc" className="consent-option-desc">
              Google Analytics 4 — lets us see which pages visitors use so we can improve them.
              No personal profile is built.
            </p>
          </fieldset>

          <fieldset className="consent-option">
            <label>
              <input
                type="checkbox"
                checked={ads}
                onChange={e => setAds(e.target.checked)}
                aria-describedby="consent-ads-desc"
              />
              <span className="consent-option-title">Advertising</span>
            </label>
            <p id="consent-ads-desc" className="consent-option-desc">
              Google Ads conversion measurement and remarketing. Helps us tell which ads bring
              real customers vs which waste budget.
            </p>
          </fieldset>

          <div className="consent-banner-actions">
            <button
              type="button"
              className="consent-btn consent-btn-secondary"
              onClick={() => setCustomizing(false)}
            >
              Back
            </button>
            <button
              type="button"
              className="consent-btn consent-btn-primary"
              onClick={() => decide(analytics, ads)}
            >
              Save my choices
            </button>
          </div>
        </div>
      ) : (
        <div className="consent-banner-actions">
          <button
            type="button"
            className="consent-btn consent-btn-secondary"
            onClick={() => decide(false, false)}
          >
            Reject non-essential
          </button>
          <button
            type="button"
            className="consent-btn consent-btn-secondary"
            onClick={() => setCustomizing(true)}
          >
            Customize
          </button>
          <button
            type="button"
            className="consent-btn consent-btn-primary"
            onClick={() => decide(true, true)}
          >
            Accept all
          </button>
        </div>
      )}
    </div>
  );
}
